import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { generatePasscode, isValidPasscode } from '@/utils/storage'
import PublicLayout from '@/components/layout/PublicLayout'
import Modal from '@/components/layout/Modal'

const screenshots = [
  { src: '/screenshots/Full-screen-drinks.png', caption: 'Live dashboard with BAC tracking, session insights, and trend charts' },
  { src: '/screenshots/add-a-drink-a.png', caption: 'Log a drink in seconds with pre-filled details' },
  { src: '/screenshots/add-a-drink-auto-complete.png', caption: 'Smart autocomplete from 65+ drinks or your own history' },
  { src: '/screenshots/milestones-awards.png', caption: 'Earn sobriety milestones from your first day to three years' },
  { src: '/screenshots/health-status.png', caption: 'Health alerts based on NHMRC guidelines' },
  { src: '/screenshots/doc-note.png', caption: 'Download a GP-ready alcohol consumption report' },
  { src: '/screenshots/further-help.png', caption: 'Region-specific helplines and resources when you need them' },
  { src: '/screenshots/password-protected-timeout.png', caption: 'Password protection with auto-lock timeout' },
  { src: '/screenshots/comprehensive-settings.png', caption: 'Full settings: password, backups, auto-logout, data management' },
]

const features = [
  {
    title: 'Real-time BAC tracking',
    description: 'Watch your blood alcohol level update live as you log each drink',
    hash: 'bac-tracking',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    title: 'GP-ready reports',
    description: 'Download a professional PDF to take to your next doctor appointment',
    hash: 'gp-reports',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    title: 'Sobriety streaks',
    description: 'See how far you\'ve come and celebrate every milestone',
    hash: 'session-insights',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.016 6.016 0 01-2.27.894m0 0a6.012 6.012 0 01-4 0m4 0a17.957 17.957 0 00-4 0" />
      </svg>
    ),
  },
  {
    title: 'Session insights',
    description: 'Understand your patterns with detailed session history and trends',
    hash: 'session-insights',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    title: 'Quick drink logging',
    description: 'Choose from 65+ pre-loaded drinks or add your own in seconds',
    hash: 'quick-logging',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'End-to-end encrypted',
    description: 'Your data is encrypted on your device. We can\'t read it — nobody can but you',
    hash: 'encryption',
    icon: (
      <svg className="w-8 h-8 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
  },
]

export default function Home() {
  const navigate = useNavigate()
  const [passcode, setPasscode] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [needsPassword, setNeedsPassword] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [statModal, setStatModal] = useState<'private' | 'bac' | 'free' | null>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)

  // Refocus the password field when verification fails (input is disabled while isChecking).
  useEffect(() => {
    if (error && needsPassword && !isChecking) {
      passwordInputRef.current?.focus()
    }
  }, [error, needsPassword, isChecking])

  const nextSlide = useCallback(() => {
    setCurrentSlide(i => (i + 1) % screenshots.length)
  }, [])

  const prevSlide = useCallback(() => {
    setCurrentSlide(i => (i - 1 + screenshots.length) % screenshots.length)
  }, [])

  // Auto-advance every 5 seconds unless paused or lightbox is open
  useEffect(() => {
    if (isPaused || lightboxOpen) return
    const timer = setInterval(nextSlide, 5000)
    return () => clearInterval(timer)
  }, [isPaused, lightboxOpen, nextSlide])

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      else if (e.key === 'ArrowRight') nextSlide()
      else if (e.key === 'ArrowLeft') prevSlide()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [lightboxOpen, nextSlide, prevSlide])

  const handleExistingPasscode = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedPasscode = passcode.trim()

    if (!trimmedPasscode) {
      setError('Please enter your passcode')
      return
    }

    if (!isValidPasscode(trimmedPasscode)) {
      setError('Invalid passcode format')
      return
    }

    // If we already know password is needed, verify it
    if (needsPassword) {
      if (!password) {
        setError('Please enter your password')
        return
      }

      setIsChecking(true)
      setError('')

      try {
        const response = await fetch(`/api/user/${trimmedPasscode}/password/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          setError((errorData as { error?: string }).error || 'Failed to verify password')
          return
        }

        const result = await response.json()

        if (result.valid) {
          // Mark session as unlocked and navigate, passing password for E2E decryption
          sessionStorage.setItem(`ssdl:unlocked:${trimmedPasscode}`, 'true')
          navigate(`/u/${trimmedPasscode}`, { state: { password } })
        } else {
          setError('Incorrect password')
        }
      } catch {
        setError('Failed to verify password')
      } finally {
        setIsChecking(false)
      }
      return
    }

    // Check if password is required
    setIsChecking(true)
    setError('')

    try {
      const response = await fetch(`/api/user/${trimmedPasscode}/auth-info`)
      const result = await response.json()

      if (result.hasPassword) {
        // Password required - show password field
        setNeedsPassword(true)
        setIsChecking(false)
        return
      }

      // No password required - navigate directly
      navigate(`/u/${trimmedPasscode}`)
    } catch {
      // In dev mode or if API fails, just navigate
      navigate(`/u/${trimmedPasscode}`)
    } finally {
      setIsChecking(false)
    }
  }

  const handleCreateNew = () => {
    const newPasscode = generatePasscode()
    navigate(`/u/${newPasscode}`)
  }

  return (
    <PublicLayout hideFooter>
      <div className="flex-1 flex flex-col items-center p-4 py-8 md:py-12">
        <div className="w-full max-w-3xl">
          {/* Hero */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-3">
              Ultra-secure drink tracking. Unmatched insights.
            </h2>
            <p className="text-[var(--text-secondary)] max-w-xl mx-auto text-lg">
              The anonymous drink tracker that helps you see clearly, cut back confidently, and have better conversations with your doctor.
            </p>
            <button
              onClick={handleCreateNew}
              className="btn btn-primary mt-6 text-base px-8 py-2.5"
            >
              Start Tracking Free
            </button>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              No sign-up. No email. Takes 5 seconds.
            </p>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mb-10">
            <button onClick={() => setStatModal('private')} className="text-center p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] hover:border-[var(--accent-teal)] transition-colors cursor-pointer">
              <div className="text-2xl font-bold text-[var(--accent-teal)]">100%</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">Private</div>
            </button>
            <button onClick={() => setStatModal('bac')} className="text-center p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] hover:border-[var(--accent-teal)] transition-colors cursor-pointer">
              <div className="text-2xl font-bold text-[var(--accent-teal)]">Live</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">BAC Tracking</div>
            </button>
            <button onClick={() => setStatModal('free')} className="text-center p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)] hover:border-[var(--accent-teal)] transition-colors cursor-pointer">
              <div className="text-2xl font-bold text-[var(--accent-teal)]">Free</div>
              <div className="text-xs text-[var(--text-secondary)] mt-1">Forever</div>
            </button>
          </div>

          {/* Privacy Banner */}
          <div className="card border-[var(--accent-teal)] text-center mb-10">
            <div className="flex justify-center mb-3">
              <svg className="w-10 h-10 text-[var(--accent-teal)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">
              End-to-End Encrypted
            </h3>
            <p className="text-[var(--text-secondary)] max-w-md mx-auto mb-3">
              Your data is encrypted on your device before it ever reaches our servers. We can't read it. Nobody can — except you.
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-[var(--text-secondary)]">
              <span>AES-256 encryption</span>
              <span>No account needed, ever!</span>
              <span>Zero knowledge</span>
              <span>No AI touches your data</span>
            </div>
          </div>

          {/* Screenshot Carousel */}
          <div className="mb-10">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] text-center mb-4">
              See It in Action
            </h3>
            <div
              className="relative card overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={() => setIsPaused(true)}
              onTouchEnd={() => setIsPaused(false)}
            >
              {/* Image container */}
              <div className="relative flex items-center justify-center bg-[var(--bg-primary)] rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
                <img
                  src={screenshots[currentSlide].src}
                  alt={screenshots[currentSlide].caption}
                  className="max-h-[500px] w-auto max-w-full object-contain cursor-pointer"
                  loading={currentSlide === 0 ? 'eager' : 'lazy'}
                  onClick={() => setLightboxOpen(true)}
                />

                {/* Prev/Next buttons */}
                <button
                  onClick={prevSlide}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[var(--bg-secondary)]/80 hover:bg-[var(--bg-card)] flex items-center justify-center transition-colors"
                  aria-label="Previous screenshot"
                >
                  <svg className="w-5 h-5 text-[var(--text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[var(--bg-secondary)]/80 hover:bg-[var(--bg-card)] flex items-center justify-center transition-colors"
                  aria-label="Next screenshot"
                >
                  <svg className="w-5 h-5 text-[var(--text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Caption */}
              <p className="text-sm text-[var(--text-secondary)] text-center mt-3">
                {screenshots[currentSlide].caption}
              </p>

              {/* Dots */}
              <div className="flex justify-center gap-1.5 mt-3">
                {screenshots.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      i === currentSlide
                        ? 'bg-[var(--accent-teal)]'
                        : 'bg-[var(--border)] hover:bg-[var(--text-secondary)]'
                    }`}
                    aria-label={`Go to screenshot ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {features.map((feature) => (
              <Link key={feature.title} to={`/features#${feature.hash}`} className="card flex items-start gap-3 hover:border-[var(--accent-teal)] transition-colors no-underline">
                <div className="shrink-0 mt-0.5">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold text-[var(--text-primary)] mb-1">{feature.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)]">{feature.description}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {/* Start Your Journey */}
            <div id="get-started" className="card border-[var(--accent-teal)]">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
                Start Your Journey
              </h2>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Create a free, anonymous log in seconds. No email or account needed — just a passcode and an optional password that enables end-to-end encryption.
              </p>
              <button
                onClick={handleCreateNew}
                className="w-full btn btn-primary"
              >
                Get Started
              </button>
              <p className="text-xs text-[var(--text-secondary)] text-center mt-2">Free forever. No credit card.</p>
            </div>

            {/* Welcome Back */}
            <div id="enter-key" className="card">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
                Welcome Back
              </h2>
              <form onSubmit={handleExistingPasscode}>
                <input
                  type="text"
                  value={passcode}
                  onChange={(e) => {
                    setPasscode(e.target.value.toUpperCase())
                    setError('')
                    setNeedsPassword(false)
                    setPassword('')
                  }}
                  placeholder="Enter your passcode"
                  className="w-full mb-3 font-mono"
                  autoComplete="off"
                  autoCapitalize="characters"
                  disabled={isChecking}
                />
                {needsPassword && (
                  <input
                    ref={passwordInputRef}
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setError('')
                    }}
                    placeholder="Enter your password"
                    className="w-full mb-3"
                    autoComplete="current-password"
                    autoFocus
                    disabled={isChecking}
                  />
                )}
                {error && (
                  <p className="text-sm text-[var(--bac-danger)] mb-3">{error}</p>
                )}
                <button type="submit" className="w-full btn btn-secondary" disabled={isChecking}>
                  {isChecking ? 'Checking...' : needsPassword ? 'Unlock' : 'Access My Log'}
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-[var(--text-secondary)] space-y-2">
            <p>
              Your data is end-to-end encrypted when you set a password. Not even we can read it.
              <br />
              <a href="/recover" className="text-[var(--accent-teal)] hover:underline">
                Lost your passcode? Try key recovery
              </a>
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/about" className="hover:text-[var(--text-primary)] transition-colors">About</Link>
              <span>|</span>
              <Link to="/security" className="hover:text-[var(--text-primary)] transition-colors">Security</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-[var(--text-primary)] transition-colors">Terms</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-[var(--text-primary)] transition-colors">Privacy</Link>
              <span>|</span>
              <a href="https://ko-fi.com/heigh" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--text-primary)] transition-colors">❤️ Buy me a Coffee<span className="sr-only"> (opens in new tab)</span></a>
            </div>
          </div>
        </div>
      </div>
      {/* Stat Detail Modals */}
      <Modal isOpen={statModal === 'private'} onClose={() => setStatModal(null)} title="100% Private">
        <div className="space-y-4 text-[var(--text-primary)]">
          <p>SSDL was built from the ground up with one principle: <strong>your drinking data is nobody's business but yours</strong>.</p>
          <div className="space-y-3">
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">No account required</p>
                <p className="text-sm text-[var(--text-secondary)]">No email, no name, no phone number. Just a passcode you create yourself. There is nothing to tie your data back to you.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">End-to-end encryption</p>
                <p className="text-sm text-[var(--text-secondary)]">When you set a password, your data is encrypted with AES-256 on your device before it ever leaves your browser. The server stores an opaque blob it cannot read.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Zero tracking</p>
                <p className="text-sm text-[var(--text-secondary)]">No analytics, no cookies, no third-party scripts. We don't know who you are, where you are, or how you use the app.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Client-side processing</p>
                <p className="text-sm text-[var(--text-secondary)]">BAC calculations, health assessments, and PDF reports are all generated in your browser. Your data never passes through our servers for processing.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">No AI touches your data</p>
                <p className="text-sm text-[var(--text-secondary)]">AI is only used to look up drink information (name, volume, ABV) when you search. Your drink log, BAC history, health data, and personal details are never sent to any AI service.</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={statModal === 'bac'} onClose={() => setStatModal(null)} title="Live BAC Tracking">
        <div className="space-y-4 text-[var(--text-primary)]">
          <p>SSDL calculates your Blood Alcohol Content in real-time using the <strong>Widmark formula</strong> — the same method used in forensic and clinical settings.</p>
          <div className="space-y-3">
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Real-time updates</p>
                <p className="text-sm text-[var(--text-secondary)]">Your BAC recalculates every second, accounting for your body weight, gender, what you drank, and metabolic decay at 0.015% per hour.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Sober countdown</p>
                <p className="text-sm text-[var(--text-secondary)]">See exactly how long until your BAC returns to zero, colour-coded by severity so you know when you're safe to drive.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Session awareness</p>
                <p className="text-sm text-[var(--text-secondary)]">Compare your current session against your last and your historical average. Spot when you're drinking more than usual — before it becomes a pattern.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Health risk assessment</p>
                <p className="text-sm text-[var(--text-secondary)]">Automatic NHMRC guideline checks flag when your drinking pattern moves from low risk to increased or high risk, with region-specific helplines and a downloadable GP report.</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={statModal === 'free'} onClose={() => setStatModal(null)} title="Free Forever">
        <div className="space-y-4 text-[var(--text-primary)]">
          <p>SSDL is and always will be <strong>completely free</strong>. No premium tier, no paywalls, no "free trial" that expires.</p>
          <div className="space-y-3">
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Every feature included</p>
                <p className="text-sm text-[var(--text-secondary)]">BAC tracking, session insights, trend charts, sobriety milestones, health alerts, GP reports, encryption, backups — all of it, for everyone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">No ads, no data selling</p>
                <p className="text-sm text-[var(--text-secondary)]">We don't monetise your data. We can't — it's encrypted. There are no ads, no sponsors, and no third-party integrations harvesting your information.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="text-[var(--accent-teal)] mt-0.5 shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </span>
              <div>
                <p className="font-medium">Built to help, not to profit</p>
                <p className="text-sm text-[var(--text-secondary)]">SSDL exists because understanding your drinking shouldn't cost money. Alcohol awareness tools should be accessible to everyone who needs them.</p>
              </div>
            </div>
          </div>
          <div className="pt-2 text-center">
            <Link
              to="/my-story"
              onClick={() => setStatModal(null)}
              className="btn btn-secondary inline-flex items-center gap-2"
            >
              Why / How?
            </Link>
          </div>
        </div>
      </Modal>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={screenshots[currentSlide].caption}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            aria-label="Close lightbox"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); prevSlide() }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            aria-label="Previous screenshot"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); nextSlide() }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            aria-label="Next screenshot"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Image + caption */}
          <div className="flex flex-col items-center max-h-[90vh] max-w-[90vw] px-12" onClick={(e) => e.stopPropagation()}>
            <img
              src={screenshots[currentSlide].src}
              alt={screenshots[currentSlide].caption}
              className="max-h-[80vh] max-w-full object-contain"
            />
            <p className="text-sm text-white/70 text-center mt-3">
              {screenshots[currentSlide].caption}
            </p>
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-3">
              {screenshots.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    i === currentSlide
                      ? 'bg-[var(--accent-teal)]'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  aria-label={`Go to screenshot ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  )
}
